import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import {
    CheckboxInput,
    FlexDiv,
    Heading2,
    Heading3,
    Heading4,
    TextDefault,
    TextLead, ThemedButton,
} from '@awarego/awarego-components';
import RadioButton from '../../RadioButton';
import moment from 'moment/moment';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { IconButton, Typography } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import makeStyles from '@mui/styles/makeStyles';
import LaunchIcon from '@mui/icons-material/Launch';
import ErrorIcon from '@mui/icons-material/Error';
import SettingMultiSelect from '../../../containers/settings/SettingMultiSelect';

const useStyles = makeStyles(() => ({
    authType: {
        display: 'flex',
        padding: '15px 0 40px 0',
        '& label.checked': {
            fontWeight: 700,
        },
        '& .container-radiobutton': {
            paddingLeft: 30,
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '24px',
            marginRight: 20,
            '& .radiomark': {
                width: 22,
                height: 22,
                '&:after': {
                    left: 2,
                    top: 2,
                    width: 14,
                    height: 14,
                },
            },
        },
    },
}));
function Settings({
          personalizeForm,
          authenticationType,
          dueDate,
          onChangeDueDate,
          onChangeAuthenticationType,
          adminTimeZone,
          emails_enabled,
          emailsForm,
          onChangeEmailsEnabled,
          customization_enabled,
          onChangeCustomizationEnabled,
          bannerImageSrc,
          bannerLinkURL,
          onAddDeleteEmail,
          onChangeBannerImageSrc,
          onChangeBannerLinkURL,
          createOptionsArray,
          enabledLanguages,
          setEnabledLanguages,
          })
    {
    const classes = useStyles();

    const [dueError, setDueError] = React.useState(null);
    const [bannerImageSrcError, setBannerImageSrcError] = React.useState(null);
    const [bannerLinkURLError, setBannerLinkURLError] = React.useState(null);

    const handleBannerImageSrcChange = (value) => {
        if(!value.startsWith('https://')) setBannerImageSrcError('Invalid url format');
        else setBannerImageSrcError(null);
        onChangeBannerImageSrc(value);
    }

    const handleOnEmailDelete = (user) => {
        user.onDel();
        onAddDeleteEmail()
    }

    const handleOnEmailAdd = async () => {
        const usersField = emailsForm.$('users');

        usersField.onAdd();

        // the new element is ALWAYS the last numeric key
        const addedIndex   = usersField.size - 1;   // size is computed in Base
        const newUserField = usersField.$(`${addedIndex}`);

        // silently mark it invalid (no red border yet)
        await newUserField.$('email')
            .validate({ showErrors: false });

        onAddDeleteEmail();
    };



    const handleBannerLinkUrlChange = (value) => {
        if(!value.startsWith('https://')) setBannerLinkURLError('Invalid url format');
        else setBannerLinkURLError(null);
        onChangeBannerLinkURL(value);
    }

    const errorMessageDueDate = React.useMemo(() => {
        switch (dueError) {
            case 'maxDate':
            case 'disablePast': {
                return 'Due date cannot be set in the past';
            }

            case 'invalidDate': {
                return 'Your date is not valid';
            }

            default: {
                return '';
            }
        }
    }, [dueError]);

    return (
        <Fragment>
            <div>
                <br />
                <Heading2 $noWrap displayBlock>
                    Set authentication*
                </Heading2>
                <Heading4 lighter mt={8} displayBlock>
                    Type of authentication
                </Heading4>
                <div className={classes.authType}>
                    <Fragment>
                        <RadioButton
                            id="typeAuth1"
                            label="Requried Email"
                            value={'required-email'}
                            groupValue={authenticationType}
                            onClick={onChangeAuthenticationType}
                        />
                        <RadioButton
                            id="typeAuth0"
                            label="No email"
                            value={'no-email'}
                            groupValue={authenticationType}
                            onClick={onChangeAuthenticationType}
                        />
                        <RadioButton
                            id="typeAuth-1"
                            label="Optional email"
                            value={'optional-email'}
                            groupValue={authenticationType}
                            onClick={onChangeAuthenticationType}
                        />
                    </Fragment>
                </div>
                <FlexDiv column maxWidth={264}>
                    <Heading2 mt={20} $noWrap displayBlock>
                        Set due date*
                    </Heading2>
                    <Heading4 lighter mt={8} mb={5} displayBlock>
                        Date & time
                    </Heading4>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            hiddenLabel={true}
                            value={dueDate ? moment(dueDate) : null}
                            onChange={onChangeDueDate}
                            ampm={false}
                            dayOfWeekFormatter={(day) => `${day}`}
                            disablePast={true}
                            format={`DD/MM/YYYY HH:mm[${adminTimeZone}]`}
                            views={['day', 'hours', 'minutes']}
                            onError={(newError) => setDueError(newError)}
                            slotProps={{
                                textField: {
                                    helperText: errorMessageDueDate,
                                    placeholder: 'Select date & time',
                                },
                            }}
                        />
                    </LocalizationProvider>
                </FlexDiv>
                <FlexDiv mt={40} alignItems="flex-start">
                    {/* Email Notifications Section */}
                    <FlexDiv column style={{ flex: '1 1 0', maxWidth: 264 }}>
                        <CheckboxInput
                            style={{height: '43px'}}
                            checked={emails_enabled}
                            onChange={onChangeEmailsEnabled}
                            name="emailNotifications"
                            label={<TextLead bold>Email(s) notifications</TextLead>}
                            noYPadding
                        />
                        {emails_enabled ? (
                            <Fragment>
                                {emailsForm.values().users.length > 0 && (
                                    <Heading3 displayBlock mt={16} mb={8}>
                                        Emails
                                    </Heading3>
                                )}
                                {emailsForm.$('users').map((user, i) => {
                                    return (
                                        <div key={user.id}>
                                            <div className="assessment-reminder-row">
                                                <div className="reminder-row-count">{i + 1}.</div>
                                                <div className={'d-flex flex-row'}>
                                                    <div className="form-holder" style={{width: '225px'}}>
                                                        <input
                                                            type="email"
                                                            name="recipientEmail"
                                                            className={
                                                                user.$('email').isValid
                                                                    ? 'bigInput dark'
                                                                    : 'bigInput '
                                                            }
                                                            {...user.$('email').bind()}
                                                        />
                                                        <div className="error">{user.$('email').error}</div>
                                                    </div>
                                                    <IconButton
                                                        aria-label="delete"
                                                        onClick={() => handleOnEmailDelete(user)}
                                                        color="secondary"
                                                        style={{width: '40px', height: '40px'}}
                                                    >
                                                        <DeleteForeverIcon />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {emailsForm.values().users.length === 0 ? <br /> : ''}
                                {emailsForm.values().users.length < 3 && (
                                    <div>
                                        <TextDefault
                                            link
                                            themed
                                            bold
                                            onClick={handleOnEmailAdd}
                                        >
                                            + Add new email
                                        </TextDefault>
                                    </div>
                                )}
                            </Fragment>
                        ) : null}
                    </FlexDiv>
                    {/* Webhook Button */}
                    <FlexDiv alignItems="flex-start" style={{ flex: '0 0 auto' }}>
                        <div className="d-flex align-items-start">
                            <a href="/settings/webhooks" target="_blank" rel="noopener noreferrer">
                                <ThemedButton
                                    mode="secondary"
                                    text="Setup Webhooks"
                                    icon={<LaunchIcon fontSize="small" color="primary" />}
                                    iconPosition="end"
                                />
                            </a>
                        </div>
                    </FlexDiv>
                </FlexDiv>
                <FlexDiv column mt={45}>
                    <CheckboxInput
                        checked={customization_enabled}
                        onChange={onChangeCustomizationEnabled}
                        name="customization"
                        label={<TextLead bold>Enable Customization</TextLead>}
                        noYPadding
                    />
                    {customization_enabled ? (
                        <Fragment>
                            <Heading4 lighter mt={20} mb={5} displayBlock>
                                Banner image URL
                            </Heading4>
                            <div key='banner-img-src'>
                                <div className="assessment-reminder-row">
                                    <div className={'d-flex flex-row'}>
                                        <div style={{width: '550px'}}>
                                            <input
                                                type="url"
                                                value={bannerImageSrc}
                                                onChange={(e) => handleBannerImageSrcChange(e.target.value)}
                                                name="brandingImageSrc"
                                                placeholder="https://example.com/image.png"
                                                className={
                                                    bannerImageSrcError
                                                        ? 'bigInput dark'
                                                        : 'bigInput '
                                                }
                                            />
                                            <div className="error">{bannerImageSrcError}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Heading4 lighter mt={8} mb={5} displayBlock>
                                Banner link URL
                            </Heading4>
                            <div key='banner-link-url'>
                                <div className="assessment-reminder-row">
                                    <div className={'d-flex flex-row'}>
                                        <div style={{width: '550px'}}>
                                            <input
                                                type="url"
                                                onChange={(e) => handleBannerLinkUrlChange(e.target.value)}
                                                value={bannerLinkURL}
                                                name="bannerLinkURL"
                                                placeholder="https://example.com"
                                                className={
                                                    bannerLinkURLError
                                                        ? 'bigInput dark'
                                                        : 'bigInput '
                                                }
                                            />
                                            <div className="error">{bannerLinkURLError}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Fragment>
                    ) : null}
                </FlexDiv>
                <SettingMultiSelect
                    name="Languages"
                    options={createOptionsArray}
                    value={enabledLanguages}
                    noSavedMark={true}
                    py={10}
                    textLead={true}
                    action={(value) => (setEnabledLanguages(value))}
                />
                {personalizeForm && !personalizeForm.isValid && (
                    <FlexDiv style={{alignItems: 'center'}} mt={80}>
                        <ErrorIcon style={{color: '#BE2024'}}/>
                        <Heading3 ml={10} lighter displayBlock style={{color: '#BE2024'}}>
                            Some fields in Personalize step are invalid!
                        </Heading3>
                    </FlexDiv>
                )}
            </div>
        </Fragment>
    );
}

export default observer(Settings);
